import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { 
  collection, 
  getDocs, 
  query, 
  where, 
  updateDoc, 
  doc, 
  getDoc,
  serverTimestamp 
} from "firebase/firestore";
import { getCachedMasterData, setCachedMasterData } from "../utils/cacheManager";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { calculateOrderTotals } from "../utils/orderUtils";
import BatchApprovalModal from "../components/BatchApprovalModal";
import { getISTDateString } from "../services/cashierService";

// --- HELPERS ---
const istStartOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const istEndOfToday = () => {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
};

const parseDate = (dateStr) => {
  if (!dateStr) return new Date(0);
  if (typeof dateStr === "object" && dateStr.seconds) {
    return new Date(dateStr.seconds * 1000);
  }
  if (typeof dateStr === "string") {
    const parts = dateStr.split("/");
    if (parts.length !== 3) return new Date(0);
    const [dd, mm, yyyy] = parts;
    return new Date(`${yyyy}-${mm}-${dd}`);
  }
  return new Date(0);
};

// --- STYLES ---
const statCardStyle = {
  background: "#ffffff",
  borderRadius: 16,
  padding: "16px 20px",
  boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)",
  border: "1px solid #e2e8f0",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center"
};

const statLabelStyle = {
  fontSize: 12,
  fontWeight: 800,
  color: "#94a3b8",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  marginBottom: 4
};

const statValueStyle = {
  fontSize: 28,
  fontWeight: 800,
  lineHeight: 1.1,
  color: "#1e293b"
};

const tabButtonStyle = (isActive) => ({
  flex: 1,
  padding: "14px 20px",
  fontSize: "14px",
  fontWeight: 700,
  border: "none",
  borderRadius: "12px",
  background: isActive ? "#ffffff" : "transparent",
  color: isActive ? "#4f46e5" : "#64748b",
  boxShadow: isActive ? "0 4px 6px -1px rgba(0,0,0,0.1)" : "none",
  cursor: "pointer",
  transition: "all 0.2s ease",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "10px"
});

const actionBtnStyle = (color = "#4f46e5") => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  padding: "8px 16px",
  fontSize: "13px",
  fontWeight: 700,
  background: color === "danger" ? "#fee2e2" : "#f1f5f9",
  color: color === "danger" ? "#991b1b" : "#475569",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  transition: "all 0.2s ease",
});

const primaryActionStyle = {
  padding: "8px 20px",
  fontSize: "13px",
  fontWeight: 700,
  background: "linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)",
  color: "#ffffff",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  boxShadow: "0 4px 6px -1px rgba(79, 70, 229, 0.3)",
};

const thStyle = {
  padding: "16px 20px",
  fontSize: "11px",
  fontWeight: 800,
  color: "#64748b",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  textAlign: "left",
  background: "#f8fafc",
  borderBottom: "1px solid #e2e8f0"
};

export default function SalesControlCenter() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("approvals"); // "approvals" or "compliance"
  const [loading, setLoading] = useState(true);

  // Shared Data
  const [partyMap, setPartyMap] = useState({});
  const [skuMaster, setSkuMaster] = useState({});

  // Compliance States
  const [complianceStats, setComplianceStats] = useState({ total: 0, approved: 0, approvedQty: 0, missing: 0 });
  const [nonCompliantPartyIds, setNonCompliantPartyIds] = useState([]);
  const [complianceSearch, setComplianceSearch] = useState("");

  // Approval States
  const [orders, setOrders] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [approvalSearch, setApprovalSearch] = useState("");

  useEffect(() => {
    loadAllData();
    const interval = setInterval(() => {
      if (!showBatchModal && activeTab === "approvals") loadApprovalData();
    }, 15000);
    return () => clearInterval(interval);
  }, [showBatchModal, activeTab]);

  const loadAllData = async () => {
    setLoading(true);
    await Promise.all([
      loadMasterData(),
      loadApprovalData(),
      loadComplianceData()
    ]);
    setLoading(false);
  };

  const loadMasterData = async () => {
    try {
      // 1. Fetch Metadata (1 read)
      const metaSnap = await getDoc(doc(db, "metadata", "masterData"));
      const meta = metaSnap.exists() ? metaSnap.data() : {};
      
      // Convert timestamps to comparable format (milliseconds)
      const skuVer = meta.skuLastUpdated?.toMillis?.() || meta.skuLastUpdated?.seconds || 0;
      const partyVer = meta.partyLastUpdated?.toMillis?.() || meta.partyLastUpdated?.seconds || 0;

      // 2. SKU Master
      let sMap = {};
      const cachedSkus = getCachedMasterData("SKU_MASTER");
      if (cachedSkus && cachedSkus.version === skuVer && skuVer !== 0) {
        sMap = cachedSkus.data;
        console.log("SCC: Loaded SKU Master from Cache");
      } else {
        const skuSnap = await getDocs(collection(db, "skuMaster"));
        skuSnap.docs.forEach((d) => {
          const data = d.data();
          if (data.sku) sMap[data.sku.trim().toUpperCase()] = data;
        });
        setCachedMasterData("SKU_MASTER", sMap, skuVer);
        console.log("SCC: Fetched SKU Master from Firestore");
      }
      setSkuMaster(sMap);

      // 3. Party Master
      let pMap = {};
      const cachedParties = getCachedMasterData("PARTY_MASTER");
      if (cachedParties && cachedParties.version === partyVer && partyVer !== 0) {
        pMap = cachedParties.data;
        console.log("SCC: Loaded Party Master from Cache");
      } else {
        const partySnap = await getDocs(collection(db, "partyMaster"));
        partySnap.docs.forEach((d) => {
          const data = d.data();
          if (data.partyId) pMap[data.partyId] = data;
        });
        setCachedMasterData("PARTY_MASTER", pMap, partyVer);
        console.log("SCC: Fetched Party Master from Firestore");
      }
      setPartyMap(pMap);

    } catch (err) {
      console.error("Error loading master data:", err);
    }
  };

  const loadApprovalData = async () => {
    try {
      const q = query(collection(db, "orders"), where("status", "==", "PENDING"));
      const snap = await getDocs(q);
      let list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => parseDate(b.createdAt) - parseDate(a.createdAt));
      setOrders(list);
    } catch (err) {
      console.error("Error loading approvals:", err);
    }
  };

  const loadComplianceData = async () => {
    try {
      // 1. Get all hawkers
      const hawkerSnap = await getDocs(
        query(collection(db, "partyMaster"), where("partytype", "==", "hawker"))
      );
      const hawkerPartyIds = hawkerSnap.docs.map(d => d.data().partyId);
      const hawkerIdSet = new Set(hawkerPartyIds);

      // 2. Get today's IST date string
      const todayIST = getISTDateString(new Date());

      // Fetch all orders today (to track who placed anything)
      const todaysOrdersSnap = await getDocs(
        query(
          collection(db, "orders"),
          where("createdAt", ">=", istStartOfToday()),
          where("createdAt", "<=", istEndOfToday())
        )
      );
      const todaysOrders = todaysOrdersSnap.docs.map(d => d.data());
      const placedPartySet = new Set(todaysOrders.map(o => o.partyId));

      // Fetch approved orders ONLY using the optimized fields
      const approvedSnap = await getDocs(
        query(
          collection(db, "orders"),
          where("status", "==", "APPROVED"),
          where("approvedDateOnly", "==", todayIST)
        )
      );

      const approvedHawkerOrders = approvedSnap.docs
        .map(d => d.data())
        .filter(o => hawkerIdSet.has(o.partyId));

      const approvedHawkerIds = new Set(approvedHawkerOrders.map(o => o.partyId));

      // Calculate Total Volume using pre-saved field
      const totalVolume = approvedHawkerOrders.reduce((acc, o) => acc + (Number(o.totalQty) || 0), 0);

      // 3. Compute missing (no order AT ALL today)
      const notPlaced = hawkerPartyIds.filter(pid => !placedPartySet.has(pid));

      setComplianceStats({
        total: hawkerPartyIds.length,
        approved: approvedHawkerIds.size,
        approvedQty: totalVolume,
        missing: notPlaced.length
      });
      setNonCompliantPartyIds(notPlaced);
    } catch (err) {
      console.error("Error loading compliance:", err);
    }
  };

  // --- APPROVAL ACTIONS ---
  const toggleSelectAll = () => {
    if (selectedIds.length === orders.length) setSelectedIds([]);
    else setSelectedIds(orders.map(o => o.id));
  };

  const toggleSelectOne = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const rejectOrder = async (orderId) => {
    try {
      const ok = window.confirm("Are you sure you want to reject this order?");
      if (!ok) return;
      await updateDoc(doc(db, "orders", orderId), {
        status: "REJECTED",
        updatedAt: serverTimestamp(),
      });
      setOrders((prev) => prev.filter((o) => o.id !== orderId));
      setSelectedIds(prev => prev.filter(x => x !== orderId));
    } catch (err) {
      console.error(err);
      alert("Failed to reject order");
    }
  };

  const exportPendingOrders = () => {
    if (!orders.length) return;
    const rows = [];
    orders.forEach((order) => {
      Object.entries(order.items || {}).forEach(([sku, qty]) => {
        if (!qty || Number(qty) === 0) return;
        rows.push({
          CreatedAt: order.createdAt?.seconds
            ? new Date(order.createdAt.seconds * 1000).toLocaleDateString("en-IN")
            : "",
          PartyId: order.partyId || "",
          PartyName: order.partyName || "",
          SKU: sku,
          Qty: Number(qty),
        });
      });
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "PendingOrders");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout]), `Pending_Orders_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  // --- COMPLIANCE ACTIONS ---
  const filteredCompliance = nonCompliantPartyIds.filter(pid => 
    pid.toLowerCase().includes(complianceSearch.toLowerCase())
  );

  // --- AGGREGATES ---
  const aggregates = orders.reduce((acc, order) => {
    const totals = calculateOrderTotals(order.items, skuMaster);
    return {
      value: acc.value + totals.tGrand,
      qty: acc.qty + totals.tQty
    };
  }, { value: 0, qty: 0 });

  const filteredOrders = orders.filter(o => 
    o.partyName?.toLowerCase().includes(approvalSearch.toLowerCase()) ||
    o.partyId?.toLowerCase().includes(approvalSearch.toLowerCase())
  );

  const batchOrders = orders.filter(o => selectedIds.includes(o.id));

  return (
    <Layout>
      <div style={{ padding: "10px 20px 40px 20px", background: "#f8fafc", minHeight: "100vh", fontFamily: "'Inter', sans-serif" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          
          {/* HEADER SECTION */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 20, flexWrap: "wrap", gap: 20 }}>
            <div style={{ borderLeft: "6px solid #4f46e5", paddingLeft: 20 }}>
              <h1 style={{ margin: 0, fontSize: 32, fontWeight: 800, color: "#1e293b", letterSpacing: "-0.025em" }}>
                Sales Control Center
              </h1>
              <p style={{ marginTop: 4, fontSize: 16, color: "#64748b", fontWeight: 500 }}>
                {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            </div>
            
            <div style={{ display: "flex", gap: 12 }}>
              <button style={actionBtnStyle()} onClick={exportPendingOrders}>
                📥 Export Pending
              </button>
              <button 
                style={actionBtnStyle()} 
                onClick={() => {
                  const pass = prompt("Enter bulk upload password");
                  if (pass === "1234bulk") navigate("/bulk-order-upload");
                  else alert("Wrong password");
                }}
              >
                ☁️ Bulk Import
              </button>
              <button style={{ ...actionBtnStyle(), background: "#0f172a", color: "#fff" }} onClick={() => navigate("/master-pending")}>
                📝 Full Edit Mode
              </button>
            </div>
          </div>

          {/* STATS GRID */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 20, marginBottom: 50 }}>
            <div style={statCardStyle}>
              <div style={statLabelStyle}>Approved Attendance</div>
              <div style={statValueStyle}>
                {complianceStats.approved} <span style={{ fontSize: 14, color: "#94a3b8", fontWeight: 500 }}>/ {complianceStats.total} Hawkers</span>
              </div>
              <div style={{ marginTop: 8, fontSize: "14px", fontWeight: 700, color: "#059669" }}>
                Volume: {complianceStats.approvedQty.toFixed(2)} M
              </div>
              <div style={{ height: 6, background: "#f1f5f9", borderRadius: 3, marginTop: 10, overflow: "hidden" }}>
                <div style={{ height: "100%", background: "#10b981", width: `${(complianceStats.approved / (complianceStats.total || 1)) * 100}%` }}></div>
              </div>
            </div>
            
            <div style={statCardStyle}>
              <div style={statLabelStyle}>Pending Approvals</div>
              <div style={{ ...statValueStyle, color: orders.length > 5 ? "#ef4444" : "#1e293b" }}>
                {orders.length} <span style={{ fontSize: 14, color: "#94a3b8", fontWeight: 500 }}>Orders Waiting</span>
              </div>
            </div>

            <div style={statCardStyle}>
              <div style={statLabelStyle}>Pending Quantity</div>
              <div style={statValueStyle}>
                {aggregates.qty.toFixed(2)} <span style={{ fontSize: 14, color: "#94a3b8", fontWeight: 500 }}>M</span>
              </div>
            </div>

            <div style={statCardStyle}>
              <div style={statLabelStyle}>Pending Value</div>
              <div style={{ ...statValueStyle, color: "#4f46e5" }}>
                ₹ {aggregates.value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>

          {/* MAIN UI TABS */}
          <div style={{ background: "#f1f5f9", padding: "6px", borderRadius: "16px", display: "flex", gap: "6px", marginBottom: "24px", maxWidth: "500px" }}>
            <button 
              style={tabButtonStyle(activeTab === "approvals")}
              onClick={() => setActiveTab("approvals")}
            >
              📦 Pending Approvals ({orders.length})
            </button>
            <button 
              style={tabButtonStyle(activeTab === "compliance")}
              onClick={() => setActiveTab("compliance")}
            >
              🚨 Missing Orders ({complianceStats.missing})
            </button>
          </div>

          {/* CONTENT AREA */}
          <div style={{ background: "#ffffff", borderRadius: 20, boxShadow: "0 10px 25px -5px rgba(0,0,0,0.05)", overflow: "hidden", border: "1px solid #e2e8f0" }}>
            
            {loading ? (
              <div style={{ padding: 100, textAlign: "center", color: "#64748b" }}>
                <div style={{ marginBottom: 16, fontSize: "24px" }}>🔄</div>
                Gearing up the dashboard...
              </div>
            ) : activeTab === "approvals" ? (
              <>
                {selectedIds.length > 0 && (
                  <div style={{ padding: "16px 24px", background: "#f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e2e8f0" }}>
                    <span style={{ fontWeight: 700, color: "#1e293b" }}>{selectedIds.length} orders selected for batch processing</span>
                    <button 
                      style={{ ...primaryActionStyle, background: "#10b981", boxShadow: "0 4px 6px -1px rgba(16, 185, 129, 0.3)" }}
                      onClick={() => setShowBatchModal(true)}
                    >
                      ⚡ Approve Selected ({selectedIds.length})
                    </button>
                  </div>
                )}

                <div style={{ padding: "16px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#fff" }}>
                  <div style={{ position: "relative", width: "100%", maxWidth: "400px" }}>
                    <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }}>🔍</span>
                    <input 
                      type="text" 
                      placeholder="Search Party Name or ID..." 
                      value={approvalSearch}
                      onChange={(e) => setApprovalSearch(e.target.value)}
                      style={{ 
                        width: "100%", padding: "10px 10px 10px 42px", borderRadius: 12, border: `2px solid ${approvalSearch ? "#4f46e5" : "#e2e8f0"}`, 
                        fontSize: 14, outline: "none", background: "#f8fafc", transition: "all 0.2s"
                      }} 
                    />
                  </div>
                  <div style={{ fontSize: "13px", fontWeight: 700, color: "#64748b" }}>
                    Showing {filteredOrders.length} of {orders.length} orders
                  </div>
                </div>

                {orders.length === 0 ? (
                  <div style={{ padding: 100, textAlign: "center" }}>
                    <div style={{ fontSize: 64, marginBottom: 20 }}>🎉</div>
                    <h2 style={{ color: "#059669", margin: 0 }}>Approval Queue Empty</h2>
                    <p style={{ color: "#64748b", marginTop: 10 }}>Every order has been processed correctly!</p>
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr>
                          <th style={{ ...thStyle, width: "40px", textAlign: "center" }}>
                            <input type="checkbox" checked={selectedIds.length === filteredOrders.length && filteredOrders.length > 0} onChange={() => {
                              if (selectedIds.length === filteredOrders.length) setSelectedIds([]);
                              else setSelectedIds(filteredOrders.map(o => o.id));
                            }} />
                          </th>
                          <th style={thStyle}>PARTY</th>
                          <th style={thStyle}>PAYMENT</th>
                          <th style={thStyle}>QTY</th>
                          <th style={thStyle}>VALUE</th>
                          <th style={thStyle}>SUBMITTED</th>
                          <th style={{ ...thStyle, textAlign: "right" }}>ACTIONS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOrders.map((order, idx) => {
                          const { tGrand: orderAmt, tQty: qty } = calculateOrderTotals(order.items, skuMaster);
                          const pData = partyMap[order.partyId] || {};
                          
                          return (
                            <tr key={order.id} style={{ borderBottom: "1px solid #f1f5f9", background: selectedIds.includes(order.id) ? "#eff6ff" : "transparent" }}>
                              <td style={{ padding: "18px 20px", textAlign: "center" }}>
                                <input type="checkbox" checked={selectedIds.includes(order.id)} onChange={() => toggleSelectOne(order.id)} />
                              </td>
                              <td style={{ padding: "18px 24px" }}>
                                <div style={{ fontWeight: 700, color: "#1e293b" }}>{order.partyName}</div>
                                <div style={{ fontSize: 12, color: "#94a3b8" }}>{order.partyId}</div>
                              </td>
                              <td style={{ padding: "18px 24px" }}>
                                <span style={{ 
                                  padding: "4px 10px", borderRadius: "6px", fontSize: "11px", fontWeight: 800, 
                                  background: pData.payment_mode === "Credit" ? "#fef3c7" : "#dcfce7", 
                                  color: pData.payment_mode === "Credit" ? "#92400e" : "#166534",
                                  textTransform: "uppercase"
                                }}>
                                  {pData.payment_mode || "Cash"}
                                </span>
                              </td>
                              <td style={{ padding: "18px 24px", color: "#64748b", fontWeight: 600 }}>{qty.toFixed(2)}</td>
                              <td style={{ padding: "18px 24px", color: "#1e293b", fontWeight: 700 }}>
                                ₹{orderAmt.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td style={{ padding: "18px 24px", color: "#94a3b8", fontSize: "13px" }}>
                                {order.createdAt?.seconds ? 
                                  `${new Date(order.createdAt.seconds * 1000).toLocaleDateString("en-IN")} ${new Date(order.createdAt.seconds * 1000).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}` 
                                  : "-"}
                              </td>
                              <td style={{ padding: "18px 24px", textAlign: "right" }}>
                                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                                  <button style={primaryActionStyle} onClick={() => navigate(`/approve-order/${order.id}`)}>
                                    Edit Order
                                  </button>
                                  <button style={actionBtnStyle("danger")} onClick={() => rejectOrder(order.id)}>
                                    Reject
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : (
              /* COMPLIANCE TAB */
              <div style={{ padding: 0 }}>
                <div style={{ padding: "24px 32px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 20 }}>
                  <h3 style={{ margin: 0, color: "#334155", fontSize: 18, fontWeight: 700 }}>
                    Missing Daily Orders
                  </h3>
                  <div style={{ position: "relative", minWidth: 250, flex: 1, maxWidth: 400 }}>
                    <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }}>🔍</span>
                    <input 
                      type="text" 
                      placeholder="Search Missing Party..." 
                      value={complianceSearch}
                      onChange={(e) => setComplianceSearch(e.target.value)}
                      style={{ 
                        width: "100%", padding: "10px 10px 10px 42px", borderRadius: 12, border: "1px solid #e2e8f0", 
                        fontSize: 14, outline: "none", background: "#f8fafc"
                      }} 
                    />
                  </div>
                </div>

                {nonCompliantPartyIds.length === 0 ? (
                  <div style={{ padding: 100, textAlign: "center" }}>
                    <div style={{ fontSize: 64, marginBottom: 20 }}>🏅</div>
                    <h2 style={{ color: "#059669", margin: 0 }}>100% Attendance Reached</h2>
                    <p style={{ color: "#64748b", marginTop: 10 }}>Every hawker has placed their order today.</p>
                  </div>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>PARTY IDENTIFIER</th>
                        <th style={thStyle}>PARTY NAME</th>
                        <th style={{ ...thStyle, textAlign: "right" }}>QUICK ACTION</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCompliance.map((pid, idx) => (
                        <tr key={pid} style={{ borderBottom: "1px solid #f1f5f9", background: idx % 2 === 0 ? "#ffffff" : "#fafafa" }}>
                          <td style={{ padding: "18px 32px", fontWeight: 700, color: "#475569" }}>{pid}</td>
                          <td style={{ padding: "18px 32px", color: "#64748b" }}>{partyMap[pid]?.name || "Loading..."}</td>
                          <td style={{ padding: "18px 32px", textAlign: "right" }}>
                            <button
                              onClick={() => navigate(`/salesman-order?partyId=${encodeURIComponent(pid)}`)}
                              style={{ ...actionBtnStyle(), background: "#4f46e5", color: "#fff" }}
                            >
                              Create Order
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

          </div>
        </div>
      </div>

      {showBatchModal && (
        <BatchApprovalModal 
          orders={batchOrders} 
          onClose={() => {
            setShowBatchModal(false);
            setSelectedIds([]);
            loadApprovalData();
            loadComplianceData();
          }}
        />
      )}
    </Layout>
  );
}
